// packages/web/src/components/marketplace/AgentSkillCard.tsx
// Card component for AI agent skills in the marketplace

import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';

import SmartToyIcon from '@mui/icons-material/SmartToy';
import GitHubIcon from '@mui/icons-material/GitHub';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React, { useState } from 'react';

export interface AgentSkill {
  serviceId: string;
  agentId: string;
  agentName?: string;
  agentRatingAvg?: number;
  agentRatingCount?: number;
  agentTotalEarningsCents?: number;
  name: string;
  description: string;
  category: string;
  pricePerCall: number;
  pricingModel: 'per_call' | 'monthly' | 'usage_based';
  serviceType: 'ai_service' | 'it_service';
  endpoint?: string;
  documentation?: string;
  status: string;
  callCount: number;
  ratingAvg: number;
  ratingCount: number;
  trustScore?: number;
  createdAt: string;
}

interface AgentSkillCardProps {
  skill: AgentSkill;
  onCardClick?: (skill: AgentSkill) => void;
}

/** Parse documentation JSON for metadata (sourceUrl, installCommand, tags, etc.) */
function parseDocMeta(skill: AgentSkill) {
  try {
    if (skill.documentation) {
      const doc = JSON.parse(skill.documentation);
      return {
        sourceUrl: doc.sourceUrl as string | undefined,
        installCommand: doc.installCommand as string | undefined,
        tags: (doc.tags || []) as string[],
        slug: doc.slug as string | undefined,
      };
    }
  } catch {}
  return { sourceUrl: undefined, installCommand: undefined, tags: [], slug: undefined };
}

const categoryColors: Record<string, string> = {
  'Developer Tools': '#42a5f5',
  'AI Agents': '#7c4dff',
  'Data & Analytics': '#26a69a',
  'Search & Research': '#ff7043',
  'Security': '#ef5350',
  'Cloud & Infrastructure': '#5c6bc0',
  'Communication': '#66bb6a',
  'Creative & Media': '#ab47bc',
  'Finance': '#ffa726',
  'Knowledge Management': '#8d6e63',
  'Automation': '#29b6f6',
  'Monitoring': '#78909c',
  'Utilities': '#90a4ae',
  'Productivity': '#4db6ac',
  'Entertainment': '#ec407a',
  'Marketing': '#ff8a65',
  'Social Media': '#42a5f5',
  'Location & Mapping': '#66bb6a',
  'Science & Healthcare': '#26c6da',
  'IoT': '#7e57c2',
  'Travel': '#ffca28',
  'Support': '#78909c',
  'Language': '#8d6e63',
  'Legal': '#90a4ae',
  'General': '#78909c',
};

export const AgentSkillCard: React.FC<AgentSkillCardProps> = ({ skill, onCardClick }) => {
  const [copied, setCopied] = useState(false);
  const handleClick = () => onCardClick?.(skill);
  const meta = parseDocMeta(skill);
  const chipColor = categoryColors[skill.category] || '#78909c';
  const isFree = skill.pricePerCall === 0;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (meta.installCommand) {
      navigator.clipboard.writeText(meta.installCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGitHub = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (meta.sourceUrl) {
      window.open(meta.sourceUrl, '_blank', 'noopener');
    }
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        height: '100%',
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Badges */}
      <Box sx={{ position: 'absolute', top: -8, right: 12, display: 'flex', gap: 0.5 }}>
        {isFree && (
          <Box
            sx={{
              bgcolor: '#00c853',
              color: 'white',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              boxShadow: '0 2px 6px rgba(0,200,83,0.4)',
            }}
          >
            FREE
          </Box>
        )}
        <Box
          sx={{
            bgcolor: '#7c4dff',
            color: 'white',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            boxShadow: '0 2px 6px rgba(124,77,255,0.4)',
          }}
        >
          <SmartToyIcon sx={{ fontSize: 12 }} />
          MCP
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 2.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Category chip */}
        <Chip
          label={skill.category}
          size="small"
          sx={{
            mb: 1,
            bgcolor: chipColor,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 22,
          }}
        />

        {/* Name */}
        <Typography
          variant="subtitle1"
          component="div"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {skill.name}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '0.8rem',
            lineHeight: 1.4,
          }}
        >
          {skill.description}
        </Typography>

        {/* Install command (if available) */}
        {meta.installCommand && (
          <Box
            sx={{
              bgcolor: 'grey.100',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.7rem',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
              $ {meta.installCommand}
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Bottom row: price + actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 'auto',
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: isFree ? 'success.main' : 'primary.main',
            }}
          >
            {isFree ? 'Free' : `$${(skill.pricePerCall / 100).toFixed(2)}`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {meta.sourceUrl && (
              <Tooltip title="View on GitHub">
                <IconButton size="small" onClick={handleGitHub} sx={{ p: 0.5 }}>
                  <GitHubIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.25 }}
            >
              Details
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AgentSkillCard;
